import type { DeckCardInput } from '../../../deck/types'
import type { DeckPlayerCardChangeAnimation } from '../../types'
import { resolvePrimaryCheckbox } from '../../helpers/primaryCheckbox'

/** Number of checkboxes in a card's primary checkbox group (largest of count/values). */
export function countPrimaryCheckboxes(card: DeckCardInput | undefined): number {
  if (!card) return 0
  const primary = resolvePrimaryCheckbox(card.frontElements, card.backElements)
  if (!primary) return 0
  const face = primary.face === 'front' ? card.frontElements : card.backElements
  const element = face[primary.elementIndex] as { count?: number; values?: boolean[] } | undefined
  if (!element) return 0
  const provided = Array.isArray(element.values) ? element.values.length : 0
  const requested = typeof element.count === 'number' ? Math.max(0, Math.floor(element.count)) : 0
  return Math.max(provided, requested)
}

/** First `checked` boxes ticked, for a card with `total` boxes. */
export const initialCheckboxValues = (total: number, checked: number) => {
  const ticked = Math.max(0, Math.min(total, Math.floor(checked)))
  return Array.from({ length: total }, (_, index) => index < ticked)
}

/**
 * Ticks the first `n` unticked boxes (click) or unticks the last `n` ticked boxes
 * (unclick). Stored values may cover only pre-ticked boxes, so pad to `total`.
 */
export function toggleCheckboxes(
  stored: boolean[],
  total: number,
  n: number,
  mode: 'click' | 'unclick',
): boolean[] {
  const values =
    stored.length >= total
      ? [...stored]
      : [...stored, ...Array<boolean>(total - stored.length).fill(false)]
  let changed = 0
  if (mode === 'click') {
    for (let i = 0; i < values.length && changed < n; i++) {
      if (!values[i]) {
        values[i] = true
        changed++
      }
    }
  } else {
    for (let i = values.length - 1; i >= 0 && changed < n; i--) {
      if (values[i]) {
        values[i] = false
        changed++
      }
    }
  }
  return values
}

/**
 * Pool a pull-next-card node draws from: the active group's pool, else (editor macro
 * preview) the whole deck. A named group with no pool falls back to the deck pool.
 */
export function selectPullPool(
  pools: Record<string, string[]>,
  activeGroupId: string | null,
  keepCardOnFlowEnd: boolean,
  deckSize: number,
): { poolId: string | null; pool: string[] } {
  const poolId =
    activeGroupId || (keepCardOnFlowEnd && (pools.__all__?.length ?? 0) > 0 ? '__all__' : null)
  const pool =
    poolId && pools[poolId]
      ? pools[poolId]
      : poolId && poolId !== '__all__' && deckSize > 0
        ? (pools.__all__ ?? [])
        : []
  return { poolId, pool }
}

/**
 * Card-change animation for a pull. 'default' follows the player/deck success setting;
 * 'confetti' keeps no transition (the caller fires confetti instead).
 */
export function pullTransition(
  pullAnimation: string | undefined,
  configured: Array<string | undefined>,
): DeckPlayerCardChangeAnimation {
  if (pullAnimation === 'confetti' || pullAnimation === 'none') return 'none'
  if (!pullAnimation || pullAnimation === 'default') {
    return (configured.find((value) => value !== undefined && value !== null) ??
      'none') as DeckPlayerCardChangeAnimation
  }
  return pullAnimation as DeckPlayerCardChangeAnimation
}
